import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { LogoIcon } from './Logo';

interface TermsAndConditionsProps {
  onBack?: () => void;
}

export function TermsAndConditions({ onBack }: TermsAndConditionsProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {onBack && (
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        <Card className="border-2 border-emerald-100 shadow-xl">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="flex justify-center mb-2">
              <LogoIcon size="lg" />
            </div>
            <CardTitle className="text-3xl">Terms and Conditions</CardTitle>
            <p className="text-sm text-muted-foreground">
              Last Updated: November 15, 2025
            </p>
          </CardHeader>

          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground">
                By accessing and using Better to Give than Receive ("BtGtR", "the Platform", "our Service"), 
                you accept and agree to be bound by these Terms and Conditions. If you do not agree to these 
                terms, please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">2. Description of Service</h2>
              <p className="text-muted-foreground">
                BtGtR is a collaborative gift-giving platform that enables multiple users ("Gifters") to 
                coordinate, contribute funds, and collectively purchase gifts for designated recipients 
                ("Giftees"). Our Service includes:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
                <li>Gift group creation and management</li>
                <li>Collaborative gift selection and ranking</li>
                <li>Payment processing and fund pooling</li>
                <li>AI-powered gift suggestions</li>
                <li>Automated gift ordering and delivery coordination</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">3. User Accounts</h2>
              <p className="text-muted-foreground mb-2">
                <strong>3.1 Registration:</strong> You must provide accurate contact information (email or phone) 
                and create an account to use our Service.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of 
                your account credentials and for all activities under your account.
              </p>
              <p className="text-muted-foreground">
                <strong>3.3 Age Requirement:</strong> You must be at least 18 years old to use BtGtR.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">4. Financial Terms</h2>
              <p className="text-muted-foreground mb-2">
                <strong>4.1 Credit Balance:</strong> Users maintain a credit balance on the Platform by adding 
                funds through PayPal. These funds can be allocated to gift groups or withdrawn.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>4.2 Contributions:</strong> When you contribute to a gift group, funds are transferred 
                from your credit balance to the group's shared pool. Contributions are non-refundable once allocated.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>4.3 Payment Processing:</strong> All payments are processed through PayPal. We do not store 
                credit card information on our servers.
              </p>
              <p className="text-muted-foreground">
                <strong>4.4 Fees:</strong> BtGtR does not charge service fees for gift group creation or participation. 
                Standard PayPal transaction fees may apply when adding funds.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">5. Gift Groups</h2>
              <p className="text-muted-foreground mb-2">
                <strong>5.1 Group Creation:</strong> Any user may create a gift group and invite others to participate.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>5.2 Participation:</strong> Invited users may accept or decline participation. Acceptance 
                implies agreement to contribute the recommended amount (if specified).
              </p>
              <p className="text-muted-foreground">
                <strong>5.3 Gift Selection:</strong> Gifters collaborate to suggest, discuss, and rank potential gifts. 
                The highest-ranked gift(s) within budget will be automatically ordered on the designated "Order By Date."
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">6. Privacy and Data</h2>
              <p className="text-muted-foreground mb-2">
                <strong>6.1 Personal Information:</strong> We collect and store your contact information, name, 
                birthdate (optional), and transaction history. We do not share your personal information with third 
                parties except as necessary to process payments and fulfill orders.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>6.2 Giftee Privacy:</strong> Giftees do not have access to gift group details, including 
                who contributed, gift suggestions, or group discussions. This maintains the surprise element.
              </p>
              <p className="text-muted-foreground">
                <strong>6.3 Communications:</strong> By using our Service, you consent to receive transactional 
                emails and SMS messages related to your gift groups, invitations, and account activity.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">7. User Conduct</h2>
              <p className="text-muted-foreground">
                You agree not to use BtGtR to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
                <li>Engage in fraudulent or illegal activities</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Impersonate others or provide false information</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated tools to scrape or extract data</li>
                <li>Purchase prohibited items (illegal goods, weapons, etc.)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">8. Intellectual Property</h2>
              <p className="text-muted-foreground">
                All content, features, and functionality of BtGtR (including but not limited to text, graphics, 
                logos, and software) are owned by us and protected by copyright, trademark, and other intellectual 
                property laws. You may not reproduce, distribute, or create derivative works without our express 
                written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">9. Disclaimers and Limitations</h2>
              <p className="text-muted-foreground mb-2">
                <strong>9.1 Service Availability:</strong> We strive for 99.9% uptime but do not guarantee 
                uninterrupted access. We are not liable for service disruptions.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>9.2 Third-Party Services:</strong> BtGtR integrates with third-party services (PayPal, 
                Amazon, email providers). We are not responsible for their performance, availability, or errors.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>9.3 AI Suggestions:</strong> Gift suggestions generated by AI are provided "as is" and 
                may not always be accurate or appropriate. Users are responsible for reviewing and approving all 
                gift selections.
              </p>
              <p className="text-muted-foreground">
                <strong>9.4 Order Fulfillment:</strong> We facilitate gift ordering through third-party retailers. 
                We are not responsible for product quality, shipping delays, or retailer errors.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">10. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                To the maximum extent permitted by law, BtGtR and its operators shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, including but not limited to lost profits, 
                lost data, or business interruption, arising from your use of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">11. Termination</h2>
              <p className="text-muted-foreground">
                We reserve the right to suspend or terminate your account at any time for violation of these Terms, 
                fraudulent activity, or any other reason we deem appropriate. Upon termination, your credit balance 
                will be refunded to your PayPal account within 30 days, minus any outstanding obligations.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">12. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may update these Terms and Conditions at any time. Continued use of the Service after changes 
                constitutes acceptance of the revised terms. We will notify users of significant changes via email.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">13. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with the laws of the United States, 
                without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">14. Contact Information</h2>
              <p className="text-muted-foreground">
                For questions about these Terms and Conditions, please contact us at:{' '}
                <a href="mailto:support@btgtr.com" className="text-emerald-600 hover:text-emerald-700 underline">
                  support@btgtr.com
                </a>
              </p>
            </section>

            <div className="pt-6 mt-8 border-t border-gray-200">
              <p className="text-xs text-center text-muted-foreground italic">
                By using Better to Give than Receive, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms and Conditions.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
